import { memo, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';
import { Button, Col, Divider, Dropdown, Row } from 'antd';
import { t } from 'i18next';
import moment from 'moment';

import { ENV_VARS } from '@/env';
import { ExpiryTimer } from '@/views/screens/AuctionScreens/components/ExpiryTimer';
import { useBidExpiryTimer } from '@/views/screens/AuctionScreens/hooks';

import { ReactComponent as MoreIcon } from '../../../../../assets/icons/dots-v.svg';
import { ReactComponent as FlagIcon } from '../../../../../assets/icons/flag-solid.svg';
import { ReactComponent as LocationPin } from '../../../../../assets/icons/location-pin-green.svg';
import { APP_ROUTES } from '../../../../../contants/routes';
import { setFilterToggle, setShipmentModal } from '../../../../../redux/common/CommonActions';
import { setShipmentData } from '../../../../../redux/shipment/ShipmentActions';
import { convertDateUTCToUserLocal, getLabelFromDate } from '../../../../../utilz/dates';
import { getTransportationModeDescription } from '../../../../../utilz/transportation-mode-utils';
import HorizontalBar from '../../../../components/Bars/HorizontalBar';
import SvgConverter from '../../../../components/Common/SvgConverter';
import CurrencyInput from '../../../../components/Inputs/CurrencyInput';
import BatchLabel from '../../../../components/Labels/BatchLabel';
import FormLabel from '../../../../components/Labels/FormLabel';
import PrimaryLabel from '../../../../components/Labels/PrimaryLabel';
import ShipperConsigneeDetailsCard from '../../../../components/Labels/ShipperConsigneeDetailsCard';
import Paper from '../../../../components/Paper/Paper';
import TMSFiledCard from '../../../common/components/TMSFiledCard';

const QuoteCard = ({ data, isSelected, getTooltipMenu }) => {
  const {
    quoteNo,
    quoteKey,
    expBidTime,
    status,
    carrierName,
    totalMileage,
    shipperCity,
    shipperCountryCode,
    consigneeCity,
    countryCode,
    rateShopCost,
    convertedCurrency,
    multiStop,
    expShipDate,
    expDeliveryDate,
    totalUnits,
    weightUom,
    totalWeight,
    urgency,
    transportationGroups,
    totalCube,
    transportationMode,
    partNumber,
    uom,
    consigneeAddr1,
    shipperAddr1,
  } = data;
  const dispatch = useDispatch();
  const {
    spotQuoteTransportationModes: { data: transportationModes, progressing: transportationModesLoading },
  } = useSelector((state) => state.common);
  const navigate = useNavigate();
  const expBidTimeDateValue = convertDateUTCToUserLocal(expBidTime, false);
  const expBidTimeValue = expBidTimeDateValue ? expBidTimeDateValue.format('MMM DD YYYY h:mma') : '-';
  const isHBUser = ENV_VARS.REACT_APP_THEME === 'halliburton';
  const transportationModeDesc = useMemo(() => {
    if (transportationMode && !transportationModesLoading) {
      return getTransportationModeDescription(transportationMode, transportationModes);
    }
    return transportationMode;
  }, [transportationMode, transportationModes, transportationModesLoading]);

  const handleShipmentClick = () => {
    dispatch(setShipmentModal(quoteKey));
    dispatch(setFilterToggle(false));
    dispatch(setShipmentData({ awardedTransportationProvider: carrierName }));
    navigate(generatePath(APP_ROUTES.SEARCH_QUOTE_ID, { quoteKey }), {
      state: { prevState: true },
    });
  };

  const { isBidExpired } = useBidExpiryTimer(moment.utc(expBidTime).toISOString());

  return (
    <Paper className={`shipment-card border-muted ${isSelected ? 'selected' : ''} mb-15`}>
      <div className="py-10" onClick={handleShipmentClick} role="button" tabIndex={0}>
        <Row className="px-15" justify="space-between" align="middle">
          <Col>
            <TMSFiledCard
              title={t('translation:default.fields.quoteNumber')}
              containerClass="d-flex align-items-center"
              labelForTestId="search-quote-quoteNumber"
            >
              <p className="ml-5 text-highlight">{quoteNo}</p>
            </TMSFiledCard>
          </Col>
          <Col>
            <TMSFiledCard
              title={t('translation:default.fields.quoteTitle')}
              containerClass="d-flex align-items-center"
              labelForTestId="search-quote-quoteTitle"
            >
              <p className="ml-5 text-highlight">{partNumber}</p>
            </TMSFiledCard>
          </Col>
          <Col>
            <TMSFiledCard
              title={t('translation:default.fields.transportationProvider.label')}
              containerClass="d-flex align-items-center"
              labelForTestId="search-quote-transportationProvider"
            >
              <p className="ml-5 text-highlight">{carrierName}</p>
            </TMSFiledCard>
          </Col>
          <Col onClick={(e) => e.stopPropagation()}>
            {getTooltipMenu ? (
              <Dropdown menu={{ items: getTooltipMenu(quoteKey) }} placement="bottomLeft">
                <Button
                  ghost
                  className="bg-none border-none w-0 p-0 shadow-none"
                  size="small"
                  shape="circle"
                  type="ghost"
                >
                  <MoreIcon />
                </Button>
              </Dropdown>
            ) : null}
          </Col>
        </Row>
        <Row>
          <Divider className="mt-10 mb-15" />
        </Row>
        {/* --------------------------- 1st row ---------------------- */}
        <Row className="mb-15 px-15" gutter={15}>
          <Col sm={3}>
            <TMSFiledCard title={t('translation:default.fields.status.label')} labelForTestId="search-quote-status">
              <BatchLabel className={`background-statusColor ${isHBUser ? 'hb-bg-color' : ''}`}>{status}</BatchLabel>
            </TMSFiledCard>
          </Col>
          <Col sm={18}>
            <Row gutter={15} className="mb-5 mt-n5 flex-nowrap">
              <Col>
                <FormLabel>
                  <SvgConverter Icon={LocationPin} color="success" />
                  <span className="ml-5" data-testid="search-quote-origin">
                    {t('translation:default.fields.origin')}
                  </span>
                </FormLabel>
              </Col>
              <Col className="flex-1 shipment-card-distance-container">
                <HorizontalBar />
                <Row justify="center" className="distance-section">
                  <Col>
                    <TMSFiledCard
                      containerClass="text-center mt-0"
                      title={t('translation:shipment.card.totalMiles')}
                      labelForTestId="search-quote-totalMiles"
                      labelClass="text-distanceColor fw-800"
                    >
                      <p className="text-black f-14">
                        <span className="text-distanceColor fw-800">{totalMileage}</span>
                      </p>
                    </TMSFiledCard>
                  </Col>
                </Row>
              </Col>
              <Col>
                <FormLabel>
                  <SvgConverter Icon={LocationPin} color="danger" />
                  <span className="ml-5" data-testid="search-quote-destination">
                    {t('translation:default.fields.destination')}
                  </span>
                </FormLabel>
              </Col>
            </Row>
            <Row gutter={15} className="flex-nowrap">
              <Col className="flex-1">
                <ShipperConsigneeDetailsCard address={shipperAddr1} city={shipperCity} country={shipperCountryCode} />
              </Col>
              <Col sm={6} />
              <Col className="text-right flex-1">
                <ShipperConsigneeDetailsCard address={consigneeAddr1} city={consigneeCity} country={countryCode} />
              </Col>
            </Row>
          </Col>
          <Col sm={3}>
            <TMSFiledCard title={t('translation:default.fields.cost')} labelForTestId="search-quote-cost">
              <p className="f-16 text-black fw-800">
                <CurrencyInput value={rateShopCost} />
                <span className="currency-label">{convertedCurrency}</span>
              </p>
            </TMSFiledCard>
          </Col>
        </Row>
        {/* --------------------------- 2nd row ---------------------- */}
        <Row className="mb-15 px-15" gutter={5}>
          <Col sm={3}>
            <TMSFiledCard
              title={t('translation:default.fields.transportationProvider.label')}
              labelForTestId="search-quote-transportationProvider"
            >
              <SvgConverter
                Icon={FlagIcon}
                color={!multiStop ? 'iconDanger' : 'iconSuccess'}
                width={22}
                height={22}
                className="ml-n2"
              />
            </TMSFiledCard>
          </Col>
          <Col sm={4}>
            <TMSFiledCard
              title={t('translation:default.fields.transportationGroup.label')}
              labelForTestId="search-quote-transportationGroup"
            >
              <p className="text-black f-14">{transportationGroups}</p>
            </TMSFiledCard>
          </Col>
          <Col sm={4}>
            <TMSFiledCard
              title={t('translation:default.fields.expectedShipDate.label')}
              labelForTestId="search-quote-expectedShipDate"
            >
              <p className="text-black f-14">{getLabelFromDate(expShipDate)}</p>
            </TMSFiledCard>
          </Col>
          <Col sm={2}>
            <TMSFiledCard title={t('translation:default.fields.quantity.label')} labelForTestId="search-quote-quantity">
              <p className="text-black f-14">{totalUnits}</p>
            </TMSFiledCard>
          </Col>
          <Col sm={2}>
            <TMSFiledCard
              title={`${t('translation:shipment.card.weightQuote')} ${weightUom ? `(${weightUom || '-'})` : ''}`}
              labelForTestId="search-quote-weightQuote"
            >
              <p className="text-black f-14">{totalWeight}</p>
            </TMSFiledCard>
          </Col>
          <Col sm={2}>
            <TMSFiledCard
              title={`${t('translation:default.fields.cube.label')} ${uom ? `(${uom || '-'})` : ''}`}
              labelForTestId="search-quote-cube"
            >
              <p className="text-black f-14">{totalCube}</p>
            </TMSFiledCard>
          </Col>
          <Col sm={4} />
          <Col sm={3}>
            <TMSFiledCard
              title={t('translation:auction.BIDExpiryDateTime')}
              labelForTestId="search-quote-BIDExpiryDateTime"
            >
              <p className="text-black f-14">{expBidTimeValue}</p>
            </TMSFiledCard>
          </Col>
          <Col sm={4} />
        </Row>
        {/* --------------------------- 3rd row ---------------------- */}
        <Row className="px-15" gutter={5}>
          <Col sm={3}>
            <TMSFiledCard title={t('translation:default.fields.mode')} labelForTestId="search-quote-mode">
              <p className="text-black f-14">{transportationModeDesc}</p>
            </TMSFiledCard>
          </Col>

          <Col sm={4}>
            <TMSFiledCard
              title={t('translation:default.fields.shipmentType.label')}
              labelForTestId="search-quote-urgency"
            >
              <p className="text-black f-14">{urgency}</p>
            </TMSFiledCard>
          </Col>
          <Col sm={4}>
            <TMSFiledCard
              title={t('translation:default.fields.expectedDeliveryDate')}
              labelForTestId="search-quote-expectedDeliveryDate"
            >
              <p className="text-black f-14">{expDeliveryDate ? getLabelFromDate(expDeliveryDate) : '-'}</p>
            </TMSFiledCard>
          </Col>
          <Col sm={2} />
          <Col sm={2} />
          <Col sm={2} />
          <Col sm={4} />
          <Col sm={3}>
            <TMSFiledCard title={t('translation:shipment.card.bidExpiry')} labelForTestId="search-quote-bidExpiry">
              <p className="f-16 text-black fw-800">
                <PrimaryLabel normal className={`${isBidExpired ? 'text-danger' : 'text-success'}`}>
                  <ExpiryTimer expiryDateTime={moment.utc(expBidTime).toISOString()} />
                </PrimaryLabel>
              </p>
            </TMSFiledCard>
          </Col>
        </Row>
      </div>
    </Paper>
  );
};

export default memo(QuoteCard);
